import { useSafeBack } from '@/lib/useSafeBack';
import { Loader2, Inbox, ChevronLeft, Phone, Mail, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";

export function cx(...a) { return a.filter(Boolean).join(" "); }

// Debounce für serverseitige Suche – vermeidet einen Request pro Tastendruck.
export function useDebounced(value, delay = 350) {
  const [d, setD] = useState(value);
  useEffect(() => { const t = setTimeout(() => setD(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return d;
}

export function SearchBar({ value, onChange, placeholder = "Kontakt suchen …", className = "", testid = "contact-search-input" }) {
  return (
    <div className={cx("app-search relative", className)}>
      <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid}
        className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:border-amber-500/40 focus:outline-none transition-colors" />
      {value && (
        <button onClick={() => onChange("")} aria-label="Suche leeren" data-testid="contact-search-clear"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function ContactLine({ phone, mobile, email, className = "" }) {
  const ctx = useAuth();
  const show = ctx ? ctx.showContactData : true;
  const tel = phone || mobile;
  if (!tel && !email) return null;
  if (show === false) {
    return (
      <div className={cx("flex flex-wrap items-center gap-x-3 gap-y-1 text-xs", className)} data-testid="contact-line-masked">
        {tel && <span className="inline-flex items-center gap-1 text-slate-500 select-none"><Phone className="w-3 h-3" />•••••••••</span>}
        {email && <span className="inline-flex items-center gap-1 text-slate-500 select-none"><Mail className="w-3 h-3" />••••••••</span>}
      </div>
    );
  }
  return (
    <div className={cx("flex flex-wrap items-center gap-x-3 gap-y-1 text-xs", className)} data-testid="contact-line">
      {tel && (
        <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()} data-testid="contact-phone"
          className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 select-all">
          <Phone className="w-3 h-3" />{tel}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()} data-testid="contact-email"
          className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 select-all break-all">
          <Mail className="w-3 h-3" />{email}
        </a>
      )}
    </div>
  );
}

export function Btn({ variant = "primary", className = "", loading, children, ...p }) {
  const base = "app-button rounded-xl px-4 py-3 font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm tap focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950";
  const vs = {
    primary: "gold-btn sweep",
    outline: "border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 hover:border-amber-400/60",
    ghost: "text-slate-300 hover:bg-white/5",
    danger: "bg-red-500/90 text-white hover:bg-red-500",
    success: "bg-emerald-500 text-[#04120b] hover:bg-emerald-400",
    dark: "bg-white/[0.04] border border-white/10 text-slate-200 hover:bg-white/[0.07] hover:border-amber-500/25",
  };
  return (
    <button data-variant={variant} className={cx(base, vs[variant], className)} {...p} disabled={loading || p.disabled}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Card({ className = "", hover, children, ...p }) {
  return <div className={cx("ls-card app-panel p-5", hover && "ls-card-hover cursor-pointer", className)} {...p}>{children}</div>;
}

export function Loading({ label = "Wird geladen …" }) {
  return (
    <div className="app-loading flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
      <Loader2 className="w-7 h-7 animate-spin text-amber-400" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function Empty({ icon: Icon = Inbox, title, subtitle, action }) {
  return (
    <div className="app-empty flex flex-col items-center justify-center py-16 text-center gap-3 fade-up" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
        <Icon className="w-7 h-7 text-amber-400" />
      </div>
      <h3 className="text-lg font-display font-semibold text-white">{title}</h3>
      {subtitle && <p className="text-sm text-slate-400 max-w-xs">{subtitle}</p>}
      {action}
    </div>
  );
}

export function PageTitle({ title, subtitle, right, back }) {
  const {user}=useAuth(); const goBack=useSafeBack(user);
  const nav = useNavigate();
  return (
    <div className="app-page-title flex flex-wrap items-start justify-between mb-6 gap-3">
      <div className="flex min-w-0 items-start gap-3">
        {back && (
          <button aria-label="Zurück" onClick={() => back === true || back === -1 ? goBack() : nav(back)} data-testid="back-button"
            className="mt-1 w-9 h-9 rounded-xl border border-amber-500/25 flex items-center justify-center text-amber-300 hover:bg-amber-500/10 shrink-0">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="font-serif text-2xl sm:text-4xl font-normal text-slate-100 tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

export function Field({ label, error, children }) {
  return (
    <label className="app-field block space-y-1.5">
      <span className="text-[11px] uppercase tracking-wider text-amber-400/80 font-medium">{label}</span>
      {children}
      {error && <span className="block text-xs text-red-400">{error}</span>}
    </label>
  );
}

export function Badge({ tone = "gold", children }) {
  const t = {
    gold: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    slate: "bg-white/5 text-slate-300 border-white/10",
  };
  return <span className={cx("app-badge inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border", t[tone])}>{children}</span>;
}

export function StatTile({ icon: Icon, label, value, tone = "gold", onClick }) {
  const tones = { gold: "text-amber-400", green: "text-emerald-400", yellow: "text-yellow-400", red: "text-red-400" };
  return (
    <button onClick={onClick} disabled={!onClick}
      className={cx("ls-card app-stat p-4 text-left w-full", onClick && "ls-card-hover")}>
      <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
        {Icon && <Icon className={cx("w-4 h-4", tones[tone])} />}{label}
      </div>
      <div className="text-2xl font-display font-bold text-white">{value}</div>
    </button>
  );
}


export function LoadError({ retry, label = 'Daten konnten nicht geladen werden.' }) {
  return <div role="alert" className="ls-card p-5 space-y-3"><p className="text-sm text-slate-300">{label}</p><p className="text-xs text-slate-400">Deine gespeicherten Daten bleiben erhalten.</p><button onClick={retry} className="gold-btn rounded-xl px-4 py-3 text-sm">Erneut laden</button></div>;
}
